import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ProtectionOrderVariant } from '@swyftx/margin/build/types';

import { Modals } from '@global-components/Modals/Modals.enum';
import { useModal } from '@global-components/Modals/useModal.hooks';

import { Big } from '@shared/safe-big';

import { observer } from 'mobx-react-lite';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';

type Props = {
  positionId: string;
};

const MarginPositionsTableActions: React.FC<Props> = observer(({ positionId }) => {
  const { account } = useMarginAccount();
  const { isPending, isClosed, lots, positionMarket } = useMarginPositionDetails({ positionId });
  const { isStalePriceData } = useMarginPrices();
  const { openModal } = useModal();
  const { t } = useTranslation('margin');

  const openDetailsModal = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      initialStep: MarginPositionDetailsStep,
      protectionOrderType?: ProtectionOrderVariant,
    ) => {
      e.preventDefault();
      e.stopPropagation();

      openModal(Modals.MarginPositionDetails, {
        initialStep,
        positionId,
        protectionOrderType,
      });
    },
    [openModal, positionId],
  );

  if (isClosed) return null;

  if (isPending)
    return (
      <FlexLayout justifyContent='end' spacing={8}>
        <Button
          variant='outlined'
          size='sm'
          disabled={!account?.active || isStalePriceData()}
          tooltip={isStalePriceData() ? t('stalePrice.tooltips.edit') : undefined}
          onClick={(e) => openDetailsModal(e, MarginPositionDetailsStep.ManageProtectionOrders)}
        >
          {t('tables.positions.actions.edit')}
        </Button>
        <Button
          variant='outlined'
          size='sm'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openModal(Modals.MarginPositionDetailsDeletePendingOrder, { positionId });
          }}
          disabled={!account?.active}
        >
          {t('tables.positions.actions.delete')}
        </Button>
      </FlexLayout>
    );

  return (
    <FlexLayout justifyContent='end' spacing={8}>
      <Button
        variant='outlined'
        size='sm'
        disabled={
          Big(positionMarket?.minLots).eq(lots) ||
          Big(positionMarket?.minLots).times(2).gt(lots) ||
          !positionMarket?.close ||
          !account?.active
        }
        tooltip={!positionMarket?.close ? t('position.tooltips.disabledClose') : ''}
        onClick={(e) => openDetailsModal(e, MarginPositionDetailsStep.PartialClosePosition)}
      >
        {t('tables.positions.actions.partial')}
      </Button>
      <Button
        variant='outlined'
        size='sm'
        onClick={(e) => openDetailsModal(e, MarginPositionDetailsStep.ClosePosition)}
        tooltip={!positionMarket?.close ? t('position.tooltips.disabledClose') : ''}
        disabled={!positionMarket?.close || !account?.active}
      >
        {t('tables.positions.actions.close')}
      </Button>
    </FlexLayout>
  );
});

export { MarginPositionsTableActions };
